import DescriptionIcon from "@material-ui/icons/Description";
/* eslint-disable no-shadow */
/* eslint-disable react/display-name */
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import BaseStepper from "../../components/modules/BaseStepper";
// import CRMRetargetingModal from "../../components/Advertisers/CRMRetargetingModal";
import Controls from "../../components/CampaignCreation/Controls";
import CustomSlideCheckBoxOption from "../../components/CampaignCreation/CustomSlideCheckBoxOption";
import EmptyCampaignTemplatesPage from "../../components/CampaignCreation/EmptyCampaignTemplatesPage";
import Loader from "../../components/elements/Loader";
import PageLayout from "../../layouts/PageLayout";
import api from "../../api";
import classes from "./CampaignFromTemplateCreatePage.module.scss";
import { BaseStep } from "../../components/modules/BaseStepper/BaseStepper";
import { CAMPAIGN_STEPS } from "../../types/campaignTypes";
import {
  PublicationListSelectionStep,
  RoleTitleSelectionStep,
  StateProvinceSelectionStep,
  TargetCompaniesSelectionStep,
  VisitorsRetargetingSegmentsSelectionStep,
  ZipCodeSelectionStep,
  steps as defaultSteps,
} from "../../constants/steps";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { createCampaign, resetCampaignToCreateState } from "../../redux/reducers/campaignToCreate";
import { initializeStateWithTemplateData } from "../../utils/initializeStateWithTemplateData";

const creationStartedPageLayoutProps = {
  heading: "Ad Campaign Creation",
  description: "",
};

const customSlidesSelectionPageLayoutProps = {
  heading: "Select template to create campaign.",
  description: "Choose a template to quickly start building your campaign.",
};

const CustomCampainCreatePage: FC = () => {
  const [customSlideOptions, setCustomSlideOptions] = useState({
    stateProvinces: false,
    zipCodes: false,
    publicationLists: false,
    companyIps: false,
    roleTitles: false,
    visitorsRetargetingSegments: false,
    // crmRetargetingSegments: false,
  });

  const dispatch = useDispatch();

  const user = useSelector((state: ReduxApplicationState) => state.user.currentUser);
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);

  const [steps, setSteps] = useState<BaseStep[]>(defaultSteps);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [hasStartedCustomCampaignCreation, setHasStartedCustomCampaignCreation] = useState(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const [templates, setTemplates] = useState<any[]>([]);

  const handleCustomSlideOptionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target;

    if (checked) {
      setSelectedTemplateId(name);
    } else if (selectedTemplateId === name) {
      setSelectedTemplateId(null);
    }

    const template = templates.find((template) => template.id === name);

    setCustomSlideOptions({
      ...customSlideOptions,
      [name]: checked,
    });

    if (template) {
      const updatedSteps = defaultSteps.slice();
      template.templateSteps.forEach((stepKey: any) => {
        switch (stepKey) {
          case CAMPAIGN_STEPS.ROLE_TITLES:
            updatedSteps.push(RoleTitleSelectionStep);
            break;
          case CAMPAIGN_STEPS.TARGET_COMPANIES:
            updatedSteps.push(TargetCompaniesSelectionStep);
            break;
          case CAMPAIGN_STEPS.PUBLICATION_LISTS:
            updatedSteps.push(PublicationListSelectionStep);
            break;
          case CAMPAIGN_STEPS.ZIP_CODES:
            updatedSteps.push(ZipCodeSelectionStep);
            break;
          case CAMPAIGN_STEPS.STATE_PROVINCES:
            updatedSteps.push(StateProvinceSelectionStep);
            break;
          case CAMPAIGN_STEPS.VISITORS_RETARGETING_SEGMENTS:
            updatedSteps.push(VisitorsRetargetingSegmentsSelectionStep);
            break;
          // CRM Re-targeting Segments disabled for now
          // case CAMPAIGN_STEPS.CRM_RETARGETING_SEGMENTS:
          //   updatedSteps.push(CRMRetargetingSegmentsSelectionStep);
          //   break;
          default:
            break;
        }
      });

      setSteps(updatedSteps);
    }
  };

  const initializeState = async () => {
    const template = templates.find((template) => template.id === selectedTemplateId);

    if (!template) return;

    await initializeStateWithTemplateData(template, dispatch, advertisers);
  };

  const handleGetStarted = () => {
    if (!selectedTemplateId) {
      return;
    }
    const isTargetCompanyStepIncluded = steps.find((step) => step.label === "Target Companies");
    if (isTargetCompanyStepIncluded) {
      localStorage.setItem("customCampaignType", "companyIps");
    } else {
      localStorage.removeItem("customCampaignType");
    }
    initializeState();
    setHasStartedCustomCampaignCreation(true);
  };

  const handleBack = () => {
    dispatch(resetCampaignToCreateState());
    setHasStartedCustomCampaignCreation(false);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTemplates = async () => {
      try {
        if (!user) return;

        setIsLoadingTemplates(true);
        const data = await api.getCampaignTemplatesByUser(user);
        if (isMounted) {
          setTemplates(data);
          setIsLoadingTemplates(false);
        }
      } catch (error) {
        console.error("Failed to fetch templates:", error);
        if (isMounted) {
          setIsLoadingTemplates(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      isMounted = false;
    };
  }, [user]);

  return !templates || isLoadingTemplates ? (
    <Loader />
  ) : templates.length !== 0 ? (
    <PageLayout
      heading={
        hasStartedCustomCampaignCreation
          ? creationStartedPageLayoutProps.heading
          : customSlidesSelectionPageLayoutProps.heading
      }
      description={
        hasStartedCustomCampaignCreation
          ? creationStartedPageLayoutProps.description
          : customSlidesSelectionPageLayoutProps.description
      }
    >
      {hasStartedCustomCampaignCreation ? (
        <BaseStepper
          steps={steps}
          handleBack={handleBack}
          lastStepNextLabel="Create Campaign"
          campaignAction={createCampaign}
        />
      ) : (
        <>
          <div className={classes.customSlideOptionsContainer}>
            {templates.map((template) => (
              <CustomSlideCheckBoxOption
                key={template.id}
                label={template.templateName}
                description={template.name}
                icon={<DescriptionIcon />}
                checked={selectedTemplateId === template.id}
                name={template.id}
                handleChange={handleCustomSlideOptionsChange}
              />
            ))}
          </div>
          <Controls handleGetStarted={handleGetStarted} disabled={!selectedTemplateId} />
          {/* <CRMRetargetingModal triggerCondition={customSlideOptions?.crmRetargetingSegments} /> */}
        </>
      )}
    </PageLayout>
  ) : (
    <EmptyCampaignTemplatesPage />
  );
};

export default CustomCampainCreatePage;
